import React, { useState, useEffect, useCallback, memo } from "react";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Users,
  Shield,
  User,
  X,
  Check,
  AlertCircle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  role: "admin";
  is_active: boolean;
  created_at: string;
  status: string;
  two_fa_preference?: string | null;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

// Separate UserForm component to prevent re-renders
const UserForm = memo(
  ({
    initialData,
    onSubmit,
    onCancel,
    isSubmitting,
    isEditing,
  }: {
    initialData: UserFormData;
    onSubmit: (data: UserFormData) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    isEditing: boolean;
  }) => {
    const [formData, setFormData] = useState<UserFormData>(initialData);

    // Reset form data when initialData changes
    useEffect(() => {
      setFormData(initialData);
    }, [initialData]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    };

    const updateFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, first_name: e.target.value }));
    };

    const updateLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, last_name: e.target.value }));
    };

    const updateEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, email: e.target.value }));
    };

    const updatePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, phone_number: e.target.value }));
    };

    return (
      <form onSubmit={handleSubmit}>
        {isEditing && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">
                Email Update Restriction
              </p>
              <p className="text-sm text-blue-700">
                For security reasons, email addresses cannot be modified when
                editing users. All other fields can be updated.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={updateFirstName}
              className={`block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base`}
              placeholder="Enter first name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.last_name}
              onChange={updateLastName}
              className={`block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base`}
              placeholder="Enter last name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={updateEmail}
                className={`block w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                  isEditing ? "bg-gray-50 cursor-not-allowed" : ""
                }`}
                placeholder="Enter email address"
                required
                disabled={isEditing}
              />
            </div>
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                Email cannot be changed for security reasons
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone_number}
                onChange={updatePhoneNumber}
                className={`block w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base`}
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {isEditing ? "Update User" : "Create User"}
              </>
            )}
          </button>
        </div>
      </form>
    );
  }
);

const AdminUsers: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isAdd = searchParams.get("is_add") === "true";
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    userId: number;
    currentStatus: boolean;
    userName: string;
  } | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageSize = 10;

  // Form data for the modal
  const [modalFormData, setModalFormData] = useState<UserFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus]);

  // Fetch users when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchTerm, filterStatus]);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };

      if (debouncedSearchTerm.trim()) {
        params.search_text = debouncedSearchTerm.trim();
      }

      if (filterStatus !== "all") {
        params.is_active = filterStatus === "active" ? 1 : 0;
      }

      const response = await ApiService.searchUsers(params);

      if (response.success) {
        setUsers(response.data);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } else {
        throw new Error(response.message || "Failed to fetch users");
      }
    } catch (err: any) {
      console.error("Error fetching users:", error);
      setError(
        err?.response?.data?.message || err?.message || "Failed to fetch users"
      );
      setUsers([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = useCallback(() => {
    setEditingUser(null);
    setModalFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (isAdd) {
      handleCreateUser();
    }
  }, [isAdd]);

  const handleEditUser = useCallback((user: User) => {
    setEditingUser(user);
    setModalFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone_number: user.phone_number || "",
    });
    setShowModal(true);
  }, []);

  const handleStatusToggle = async (
    userId: number,
    currentStatus: boolean,
    userName: string
  ) => {
    setConfirmAction({ userId, currentStatus, userName });
    setShowConfirmModal(true);
  };

  const executeStatusToggle = async () => {
    if (!confirmAction) return;

    const { userId, currentStatus } = confirmAction;
    const action = currentStatus ? "deactivate" : "activate";

    try {
      setIsSubmitting(true);
      await ApiService.updateUserStatus(userId, action);
      fetchUsers();
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (err: any) {
      console.error("Error updating user status:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update user status"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (formData: UserFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (editingUser) {
        // Update existing user - exclude email from the update payload
        const updatePayload = {
          user_id: editingUser.user_id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: formData.phone_number,
          // Don't include email in update payload
        };

        await ApiService.updateUser(updatePayload);
      } else {
        // Create new user - include all fields
        await ApiService.createUser(formData);
      }

      setShowModal(false);
      fetchUsers();
    } catch (err: any) {
      console.error("Error saving user:", error);
      setError(
        err?.response?.data?.message || err?.message || "Failed to save user"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterStatus(e.target.value as "all" | "active" | "inactive");
    },
    []
  );

  // Enhanced Modal Component
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = "max-w-lg",
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: string;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className={`relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full ${maxWidth} max-h-[90vh] overflow-y-auto sm:p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-6">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>{children}</div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Confirmation Modal Component
  const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    action,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    action: {
      userId: number;
      currentStatus: boolean;
      userName: string;
    } | null;
  }) => {
    if (!isOpen || !action) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className="relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-md sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start mb-4 sm:mb-6">
              <div
                className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 ${
                  action.currentStatus ? "bg-red-100" : "bg-green-100"
                }`}
              >
                {action.currentStatus ? (
                  <UserX className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                ) : (
                  <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                  {action.currentStatus ? "Deactivate" : "Activate"} User
                </h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  This action can be reversed later
                </p>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                Are you sure you want to{" "}
                <span
                  className={`font-semibold ${
                    action.currentStatus ? "text-red-600" : "text-green-600"
                  }`}
                >
                  {action.currentStatus ? "deactivate" : "activate"}
                </span>{" "}
                the user{" "}
                <span className="font-semibold text-gray-900">
                  "{action.userName}"
                </span>
                ?
              </p>

              <div
                className={`p-3 sm:p-4 rounded-lg ${
                  action.currentStatus
                    ? "bg-red-50 border border-red-200"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <p
                  className={`text-xs sm:text-sm ${
                    action.currentStatus ? "text-red-700" : "text-green-700"
                  }`}
                >
                  {action.currentStatus
                    ? "This user will not be able to access the platform."
                    : "This user will be able to access the platform."}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center ${
                  action.currentStatus
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {action.currentStatus ? "Deactivating..." : "Activating..."}
                  </>
                ) : (
                  <>{action.currentStatus ? "Deactivate" : "Activate"} User</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile-friendly User Card Component - FIXED
  const UserCard = ({ user }: { user: User }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3 w-full overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-blue-700">
              {user.first_name.charAt(0)}
              {user.last_name.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {user.first_name} {user.last_name}
              </h3>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getRoleColor(
                  user.role
                )}`}
              >
                <span className="mr-1">{getRoleIcon(user.role)}</span>
                {user.role.replace("_", " ").toUpperCase()}
              </span>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-gray-600 flex items-center min-w-0">
                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{user.email}</span>
              </div>
              {user.phone_number && (
                <div className="text-sm text-gray-600 flex items-center min-w-0">
                  <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{user.phone_number}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-3 gap-2">
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                  user.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {user.is_active ? "Active" : "Inactive"}
              </span>
              <span className="text-xs text-gray-500 truncate">
                {user.two_fa_preference || "—"}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Created: {formatDate(user.created_at)}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={() => handleEditUser(user)}
            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() =>
              handleStatusToggle(
                user.user_id,
                user.is_active,
                `${user.first_name} ${user.last_name}`
              )
            }
            className={`p-2 rounded-lg transition-colors ${
              user.is_active
                ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                : "text-green-600 hover:text-green-900 hover:bg-green-50"
            }`}
            title={user.is_active ? "Deactivate" : "Activate"}
          >
            {user.is_active ? (
              <UserX className="h-4 w-4" />
            ) : (
              <UserCheck className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Users">
      {/* Main container with proper overflow handling */}
      <div className="space-y-4 sm:space-y-6 w-full min-w-0">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div className="text-sm text-red-700 break-words">{error}</div>
              <button
                onClick={() => setError(null)}
                className="text-red-700 hover:text-red-900 ml-4 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Admin Users
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Total {totalItems}
            </p>
          </div>
          <button
            onClick={handleCreateUser}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm sm:text-base flex-shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
            <div className="sm:w-48 flex-shrink-0">
              <select
                value={filterStatus}
                onChange={handleStatusFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        2FA Preference
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                              <span className="text-sm font-medium text-blue-700">
                                {user.first_name.charAt(0)}
                                {user.last_name.charAt(0)}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{user.email}</span>
                              </div>
                              {user.phone_number && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">
                                    {user.phone_number}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                              user.role
                            )}`}
                          >
                            <span className="mr-1">
                              {getRoleIcon(user.role)}
                            </span>
                            {user.role.replace("_", " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.two_fa_preference || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusToggle(
                                  user.user_id,
                                  user.is_active,
                                  `${user.first_name} ${user.last_name}`
                                )
                              }
                              className={`p-1 ${
                                user.is_active
                                  ? "text-red-600 hover:text-red-900"
                                  : "text-green-600 hover:text-green-900"
                              }`}
                              title={user.is_active ? "Deactivate" : "Activate"}
                            >
                              {user.is_active ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View - FIXED CONTAINER */}
              <div className="lg:hidden p-4 space-y-4 w-full overflow-hidden">
                {users.map((user) => (
                  <UserCard key={user.user_id} user={user} />
                ))}
              </div>

              {users.length === 0 && !isLoading && (
                <div className="text-center py-12 px-4">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No users found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {debouncedSearchTerm || filterStatus !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Get started by creating your first user."}
                  </p>
                  {!debouncedSearchTerm && filterStatus === "all" && (
                    <button
                      onClick={handleCreateUser}
                      className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Enhanced Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
          onConfirm={executeStatusToggle}
          action={confirmAction}
        />

        {/* Enhanced User Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingUser ? "Edit User" : "Create New Admin User"}
        >
          <UserForm
            initialData={modalFormData}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
            isSubmitting={isSubmitting}
            isEditing={!!editingUser}
          />
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
